import logger from '@utils/logger';
import {
  GetAssortmentSwapRecommendationsInput,
  useGetAssortmentSwapRecommendationsQuery,
} from '@graphql/generated/graphql';
import { useEffect, useMemo } from 'react';
import { useAppSelector } from '@store/hooks';

import { selectProgram } from '@store/slices/programSlice';

import { AgileRecommendationResult, AgileRecommendationTypes } from '@components/agile/types/AgileRecommendationTypes';

const useGetSwapRecommendationsResults = (input: GetAssortmentSwapRecommendationsInput) => {
  const selectedProgramName = useAppSelector(selectProgram);
  const { ...restInput } = input;
  const result = useGetAssortmentSwapRecommendationsQuery(
    {
      input: {
        ...restInput,
      },
    },
    {
      // disable refetchOnWindowFocus so that data is not refreshed
      // when user leave the page and come back
      refetchOnWindowFocus: false,
    },
  );
  const unitsToRemove = Number(result.data?.getAssortmentSwapRecommendations.facingUnitsToRemove);
  const maxFacingUnits = Number(result.data?.getAssortmentSwapRecommendations.maxFacingUnits);
  const occupiedFacingUnits = Number(result.data?.getAssortmentSwapRecommendations.occupiedFacingUnits);
  const results: AgileRecommendationTypes[] | undefined = useMemo(
    () =>
      result.data?.getAssortmentSwapRecommendations?.swapRecommendations?.map<AgileRecommendationTypes>((item) => ({
        entryId: item.entryId,
        asin: item.asin,
        subcategoryId: item.subcategoryId ?? undefined, // Include subcategoryId for filtering
        availableFacings: Number(item.facingUnits),
        facingToRemove: 0,
        fohCapacity: Number(item.fohCapacity),
        title: item.asinMetadata?.title ?? 'Unknown',
        image: item.asinMetadata?.image,
        uphl1: item.asinMetadata?.uph?.uphl1 ?? 'Unknown',
        uphl2: item.asinMetadata?.uph?.uphl2 ?? 'Unknown',
        uphl3: item.asinMetadata?.uph?.uphl3 ?? 'Unknown',
        shrinkCost: item.asinMetadata?.performanceData?.shrinkCost ?? 'N/A',
        shrinkPerOps: item.asinMetadata?.performanceData?.shrinkPerOps ?? 'N/A',
        shrinkPerOps13week: item.asinMetadata?.performanceData?.shrinkPerOps13week ?? 'N/A',
        shrinkPerOps26week: item.asinMetadata?.performanceData?.shrinkPerOps26week ?? 'N/A',
        shrinkPerOps52week: item.asinMetadata?.performanceData?.shrinkPerOps52week ?? 'N/A',
        movingAvgSales13week: item.asinMetadata?.performanceData?.movingAvgSales13week ?? 'N/A',
        movingAvgSales26week: item.asinMetadata?.performanceData?.movingAvgSales26week ?? 'N/A',
        movingAvgSales52week: item.asinMetadata?.performanceData?.movingAvgSales52week ?? 'N/A',
        remainingFacings: Number(item.facingUnits),
        remainingCapacity: Number(item.fohCapacity),
        difference: 0,
      })),
    [result.data?.getAssortmentSwapRecommendations],
  );

  useEffect(() => {
    if (!result.isSuccess) {
      void logger.error(`Error occurred while retrieving GetSwapRecommendation for ${selectedProgramName}`);
    }
  }, [result.isSuccess, selectedProgramName]);

  return {
    results,
    unitsToRemove,
    maxFacingUnits,
    occupiedFacingUnits,
  } as AgileRecommendationResult;
};

export default useGetSwapRecommendationsResults;
